import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Swords, Trophy, Flame, TrendingUp, ArrowLeft, Loader2, Crown,
  Medal, Zap, ChevronRight, Mic, Star
} from 'lucide-react';
import UserAvatar from '../components/UserAvatar';

const PERIOD_OPTIONS = [
  { key: 'all', label: 'All Time' },
  { key: 'monthly', label: 'This Month' },
  { key: 'weekly', label: 'This Week' },
];

function RankBadge({ rank }) {
  if (rank === 1) return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30 ring-2 ring-amber-300/50">
      <Crown className="w-5 h-5 text-white" />
    </div>
  );
  if (rank === 2) return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-lg shadow-slate-400/20 ring-2 ring-slate-300/50">
      <Medal className="w-5 h-5 text-white" />
    </div>
  );
  if (rank === 3) return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20 ring-2 ring-orange-400/50">
      <Medal className="w-5 h-5 text-white" />
    </div>
  );
  return (
    <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
      <span className="text-sm font-bold text-muted-foreground">#{rank}</span>
    </div>
  );
}

function TopThreeCard({ entry, index }) {
  const navigate = useNavigate();
  const gradients = [
    'from-amber-500/15 via-yellow-500/10 to-orange-500/5 border-amber-500/25',
    'from-slate-400/10 via-slate-300/5 to-slate-200/5 border-slate-400/20',
    'from-orange-500/10 via-orange-400/5 to-amber-500/5 border-orange-400/20',
  ];
  const glows = [
    'shadow-amber-500/10',
    'shadow-slate-400/5',
    'shadow-orange-400/5',
  ];
  const crownColors = ['text-amber-400', 'text-slate-400', 'text-orange-400'];

  return (
    <Card
      className={`relative overflow-hidden bg-gradient-to-br ${gradients[index]} border p-5 cursor-pointer 
        hover:shadow-xl ${glows[index]} transition-all duration-300 group`}
      onClick={() => navigate(`/u/${entry.handle || entry.user_id}`)}
      data-testid={`top-${index + 1}-card`}
    >
      {/* Rank indicator */}
      <div className="absolute top-3 right-3">
        <RankBadge rank={index + 1} />
      </div>

      <div className="flex items-center gap-4">
        <div className="relative">
          <UserAvatar
            src={entry.avatar}
            alt={entry.name}
            size="xl"
            userId={entry.user_id}
            userName={entry.name}
            className={`ring-2 ${index === 0 ? 'ring-amber-400/50' : index === 1 ? 'ring-slate-400/50' : 'ring-orange-400/50'}`}
          />
          {entry.streak > 0 && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white shadow-md">
              <Flame className="w-3.5 h-3.5" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-foreground text-base truncate group-hover:text-primary transition-colors">
            {entry.name}
          </h3>
          <p className="text-xs text-muted-foreground">@{entry.handle}</p>
          <div className="flex items-center gap-3 mt-2">
            <div className="text-center">
              <p className="text-lg font-black text-foreground leading-none">{entry.battle_count}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Battles</p>
            </div>
            <div className="w-px h-8 bg-border/50" />
            <div className="text-center">
              <p className="text-lg font-black text-foreground leading-none">{entry.wins}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Wins</p>
            </div>
            <div className="w-px h-8 bg-border/50" />
            <div className="text-center">
              <p className="text-lg font-black text-foreground leading-none">{entry.streak}d</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Streak</p>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement score */}
      <div className="mt-3 pt-3 border-t border-border/20 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Zap className={`w-3.5 h-3.5 ${crownColors[index]}`} />
          <span className="font-bold">{entry.engagement_score.toLocaleString()}</span>
          <span>engagement</span>
        </div>
        {entry.win_rate > 0 && (
          <Badge variant="outline" className="text-[10px] font-mono">
            {entry.win_rate}% W/R
          </Badge>
        )}
      </div>
    </Card>
  );
}

function LeaderboardRow({ entry }) {
  const navigate = useNavigate();

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/30 cursor-pointer transition-colors group"
      onClick={() => navigate(`/u/${entry.handle || entry.user_id}`)}
      data-testid={`leaderboard-row-${entry.rank}`}
    >
      <RankBadge rank={entry.rank} />

      <UserAvatar
        src={entry.avatar}
        alt={entry.name}
        size="md"
        userId={entry.user_id}
        userName={entry.name}
        className="ring-1 ring-border"
      />

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-foreground truncate group-hover:text-primary transition-colors">
          {entry.name}
        </p>
        <p className="text-xs text-muted-foreground">@{entry.handle}</p>
      </div>

      <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
        <div className="text-center w-14">
          <p className="font-bold text-foreground">{entry.battle_count}</p>
          <p className="text-[10px]">Battles</p>
        </div>
        <div className="text-center w-14">
          <p className="font-bold text-foreground">{entry.wins}</p>
          <p className="text-[10px]">Wins</p>
        </div>
        {entry.streak > 0 && (
          <div className="flex items-center gap-1">
            <Flame className="w-3.5 h-3.5 text-orange-400" />
            <span className="font-bold text-orange-400">{entry.streak}d</span>
          </div>
        )}
      </div>

      <div className="text-right min-w-[60px]">
        <p className="font-bold text-sm text-foreground">{entry.engagement_score.toLocaleString()}</p>
        <p className="text-[10px] text-muted-foreground">pts</p>
      </div>

      <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary transition-colors" />
    </div>
  );
}

export default function BattleLeaderboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('all');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myStats, setMyStats] = useState(null);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/battles/leaderboard?period=${period}&limit=25`);
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [period]);

  const fetchMyStats = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API}/battles/stats/${user.user_id}`);
      if (res.ok) setMyStats(await res.json());
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);
  useEffect(() => { fetchMyStats(); }, [fetchMyStats]);

  const topThree = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  return (
    <div className="min-h-screen bg-background" data-testid="battle-leaderboard-page">
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground tracking-tight flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                <Swords className="w-6 h-6 text-orange-400" />
                Battle Leaderboard
              </h1>
              <p className="text-sm text-muted-foreground">Who runs the DMV?</p>
            </div>
          </div>
          <Button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('openVoiceRecorder'));
              navigate('/feed');
            }}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold"
            data-testid="drop-battle-btn"
          >
            <Mic className="w-4 h-4 mr-1.5" /> Drop a Battle
          </Button>
        </div>

        {/* Period Filter */}
        <div className="flex gap-1 bg-muted/30 rounded-xl p-1 mb-6" data-testid="period-filter">
          {PERIOD_OPTIONS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                period === p.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* My Stats Banner */}
        {myStats && myStats.battle_count > 0 && (
          <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/20 p-4 mb-6" data-testid="my-battle-stats">
            <div className="flex items-center gap-4">
              <UserAvatar src={myStats.avatar} alt={myStats.name} size="lg" userId={myStats.user_id} userName={myStats.name} className="ring-2 ring-orange-400/30" />
              <div className="flex-1">
                <p className="text-sm font-bold text-foreground">Your Battle Record</p>
                <div className="flex items-center gap-4 mt-1">
                  <span className="text-xs text-muted-foreground"><b className="text-foreground">{myStats.battle_count}</b> battles</span>
                  <span className="text-xs text-muted-foreground"><b className="text-foreground">{myStats.wins}</b> wins</span>
                  <span className="text-xs text-muted-foreground"><b className="text-foreground">{myStats.total_likes}</b> likes</span>
                  {myStats.streak > 0 && (
                    <span className="text-xs text-orange-400 font-bold flex items-center gap-1">
                      <Flame className="w-3 h-3" /> {myStats.streak}d streak
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : leaderboard.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-orange-500/10 flex items-center justify-center">
              <Swords className="w-10 h-10 text-orange-400" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">No Battles Yet</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              Be the first to drop a battle post! Record a freestyle, send a message, or challenge someone.
            </p>
            <Button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('openVoiceRecorder'));
                navigate('/feed');
              }}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold"
            >
              <Mic className="w-4 h-4 mr-2" /> Start the First Battle
            </Button>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {topThree.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="top-three">
                {topThree.map((entry, i) => (
                  <TopThreeCard key={entry.user_id} entry={entry} index={i} />
                ))}
              </div>
            )}

            {/* Rest of Leaderboard */}
            {rest.length > 0 && (
              <Card className="border-border/50 bg-card divide-y divide-border/30" data-testid="leaderboard-table">
                {rest.map(entry => (
                  <LeaderboardRow key={entry.user_id} entry={entry} />
                ))}
              </Card>
            )}
          </>
        )}

        {/* How scoring works */}
        <Card className="mt-8 p-5 border-border/30 bg-muted/10" data-testid="scoring-info">
          <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-400" /> How Scoring Works
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            {[
              { label: 'Battle Reply', points: '+10', icon: Mic },
              { label: 'Comment', points: '+5', icon: Swords },
              { label: 'Repost', points: '+4', icon: TrendingUp },
              { label: 'Like', points: '+3', icon: Flame },
            ].map(s => (
              <div key={s.label} className="bg-card/50 rounded-lg p-3 border border-border/20">
                <s.icon className="w-4 h-4 text-orange-400 mx-auto mb-1" />
                <p className="text-sm font-bold text-foreground">{s.points}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
